import { ComponentType, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import Loader from '../Loader';
import { Pagination } from '../Pagination';
import { TooltipWrapper } from '../TooltipWrapper';
import { EmptyTable } from './EmptyTable';
import styles from './index.module.scss';

export interface ITableHeaderCell {
  name: string;
  tooltip?: string;
}

interface CustomTableProps<T> {
  data: Array<T>;
  tableHeaders: Array<ITableHeaderCell>;
  emptyMessage: string;
  isLoading?: boolean;
  TableRow: ComponentType<T>;
  onPaginationNext?: () => void;
  onPaginationPrev?: () => void;
  page?: number;
  tableTitle?: string | ReactNode;
  tableHeight?: number;
}

export const CustomTable = <T,>({
  data,
  emptyMessage,
  tableHeaders,
  isLoading,
  TableRow,
  page,
  tableTitle,
  tableHeight,
  onPaginationNext,
  onPaginationPrev,
}: CustomTableProps<T>) => {
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleHeight, setTitleHeight] = useState(0);
  const result = {
    loader: <Loader />,
    data: data?.map((el, index) => <TableRow key={index} {...el} />),
  };

  useLayoutEffect(() => {
    if (typeof tableHeight === 'number' && tableTitle) {
      setTitleHeight(titleRef.current?.clientHeight as number);
    } else {
      setTitleHeight(0);
    }
  }, [titleRef.current, tableHeight, tableTitle]);

  if (!data?.length && !isLoading) {
    return <EmptyTable tableTitle={tableTitle} emptyMessage={emptyMessage} />;
  }

  return (
    <>
      <div className={styles.tableWrapper}>
        {tableTitle && (
          <div className={styles.tableTitle} ref={titleRef}>
            {tableTitle}
          </div>
        )}
        <div
          className={styles.tableWrapperContent}
          style={{ height: tableHeight ? tableHeight - titleHeight : 'auto' }}
        >
          <table className={styles.table}>
            <thead>
              <tr>
                {tableHeaders?.map(({ name, tooltip }) => (
                  <th key={name}>
                    {name}
                    {tooltip && <TooltipWrapper tooltip={tooltip} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <>{data?.length > 0 && !isLoading && result.data}</>
            </tbody>
          </table>
          {isLoading && result.loader}
        </div>
      </div>
      {onPaginationNext && onPaginationPrev && page && !isLoading && (
        <Pagination<T>
          onNextPage={onPaginationNext}
          onPrevPage={onPaginationPrev}
          page={page}
          data={data}
        />
      )}
    </>
  );
};
